import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Loader2, Check, ChevronRight, Building2, Users, Hand } from 'lucide-react';

function WaitBadge({ days }) {
  const cls = days >= 7
    ? 'border-red-500/30 bg-red-500/10 text-red-400'
    : days >= 3
      ? 'border-amber-500/30 bg-amber-500/10 text-amber-400'
      : 'border-border bg-transparent text-muted-foreground';
  return (
    <span className={`text-[9px] font-mono border rounded px-1.5 py-0.5 shrink-0 ${cls}`} data-testid="waiting-days-badge">
      {days === 0 ? 'today' : `${days}d waiting`}
    </span>
  );
}

function GroupHeader({ group, isMe }) {
  if (group.type === 'external') {
    return (
      <>
        <div className="w-6 h-6 rounded-full bg-sky-600/20 flex items-center justify-center shrink-0">
          <Building2 className="w-3.5 h-3.5 text-sky-400" />
        </div>
        <p className="font-heading text-base text-foreground">{group.name}</p>
        <span className="text-[10px] text-muted-foreground">ball is with the companies</span>
      </>
    );
  }
  if (group.type === 'team') {
    return (
      <>
        <div className="w-6 h-6 rounded-full bg-purple-600/20 flex items-center justify-center shrink-0">
          <Users className="w-3.5 h-3.5 text-purple-400" />
        </div>
        <p className="font-heading text-base text-foreground">{group.name}</p>
      </>
    );
  }
  return (
    <>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold font-mono shrink-0 ${isMe ? 'bg-emerald-600/25 text-emerald-400' : 'bg-blue-600/20 text-blue-400'}`}>
        {group.name?.charAt(0)?.toUpperCase() || '?'}
      </div>
      <p className="font-heading text-base text-foreground">{group.name}</p>
      {isMe && (
        <span className="text-[9px] font-mono uppercase border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 rounded px-1.5 py-0.5" data-testid="its-you-badge">
          You
        </span>
      )}
    </>
  );
}

export default function WhosTurnPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [cold, setCold] = useState({});
  const [coldDays, setColdDays] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState({});

  const load = useCallback((withSpinner = false) => {
    if (withSpinner) setLoading(true);
    api.getWhosTurn()
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
    api.getColdEngagement().then(res => {
      const map = {};
      (res.data.deals || []).forEach(d => { map[d.project_id] = d; });
      setCold(map);
      setColdDays(res.data.cold_days);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(true); }, [load]);

  const completeTask = async (taskId) => {
    setCompleting(s => ({ ...s, [taskId]: true }));
    try {
      await api.completeTask(taskId);
      toast.success('Task completed');
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not complete the task — open the deal for details');
    }
    setCompleting(s => ({ ...s, [taskId]: false }));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const groups = data?.groups || [];

  return (
    <div className="space-y-6 max-w-4xl" data-testid="whos-turn-page">
      <div>
        <h1 className="font-heading text-2xl text-foreground tracking-tight flex items-center gap-2">
          <Hand className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} /> Who's Turn?
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {data?.total_deals || 0} active deal{data?.total_deals !== 1 ? 's' : ''} — who each deal is waiting on, at a glance
        </p>
      </div>

      {groups.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="whos-turn-summary">
          {groups.map(g => (
            <div key={g.key} className={`flex items-center gap-1.5 border rounded-sm px-2.5 py-1.5 text-xs ${g.key === data?.me ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-border bg-card'}`} data-testid={`summary-chip-${g.key}`}>
              <span className="font-medium text-foreground">{g.name}</span>
              <span className="font-mono text-muted-foreground">{g.count}</span>
              {g.max_waiting_days >= 7 && <span className="w-1.5 h-1.5 rounded-full bg-red-500" title="Waiting 7+ days" />}
            </div>
          ))}
        </div>
      )}

      {groups.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-12 text-center">
          <Check className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-foreground font-medium">No active deals</p>
          <p className="text-xs text-muted-foreground mt-1">Nothing in the active pipeline right now.</p>
        </div>
      ) : (
        groups.map(group => {
          const isMe = group.key === data?.me;
          return (
            <div key={group.key} className={`bg-card border rounded-sm ${isMe ? 'border-emerald-500/30' : 'border-border'}`} data-testid={`whos-turn-group-${group.key}`}>
              <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <GroupHeader group={group} isMe={isMe} />
                <span className="text-[10px] text-muted-foreground font-mono ml-auto">{group.count} deal{group.count !== 1 ? 's' : ''}</span>
              </div>
              {group.deals.map(deal => (
                <div key={`${group.key}-${deal.id}`} className="px-4 py-3 border-b border-border last:border-b-0 flex items-center gap-3" data-testid={`whos-turn-deal-${deal.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => navigate(`/projects/${deal.id}`)}
                        className="text-sm font-medium text-foreground hover:text-blue-400 transition-colors truncate"
                        data-testid={`whos-turn-deal-link-${deal.id}`}>
                        {deal.code_name}
                        {deal.company_name && deal.company_name.toLowerCase() !== (deal.code_name || '').toLowerCase() && (
                          <span className="text-muted-foreground font-normal"> ({deal.company_name})</span>
                        )}
                      </button>
                      {deal.on_agenda && (
                        <span className="text-[9px] font-mono uppercase border border-violet-500/40 bg-violet-500/10 text-violet-400 rounded px-1.5 py-0.5 shrink-0"
                          title="On the team meeting agenda — waiting to be discussed, no action needed"
                          data-testid={`agenda-badge-${deal.id}`}>
                          Agenda
                        </span>
                      )}
                      <span className="text-[9px] font-mono border border-border rounded px-1.5 py-0.5 text-muted-foreground">{deal.stage_label}</span>
                      {cold[deal.id] && (
                        <span className="text-[9px] font-mono uppercase border border-cyan-500/40 bg-cyan-500/10 text-cyan-400 rounded px-1.5 py-0.5 shrink-0"
                          title={`No owner/shareholder contact for ${cold[deal.id].days} days (threshold: ${coldDays}d)${cold[deal.id].never ? ' — no owner touchpoint logged yet' : ` — last: ${cold[deal.id].last_contact}`}`}
                          data-testid={`cold-badge-${deal.id}`}>
                          Cold {cold[deal.id].days}d
                        </span>
                      )}
                      <WaitBadge days={deal.waiting_days || 0} />
                    </div>
                    {(deal.tasks || []).length > 0 ? (
                      <div className="mt-1 space-y-1">
                        {deal.tasks.map(t => (
                          <div key={t.id} className="flex items-center gap-2" data-testid={`whos-turn-task-${t.id}`}>
                            <button onClick={() => completeTask(t.id)} disabled={!!completing[t.id]}
                              className="shrink-0 w-4 h-4 rounded-full border border-border hover:border-emerald-500 hover:bg-emerald-500/15 flex items-center justify-center text-transparent hover:text-emerald-400 transition-colors disabled:opacity-50"
                              title="Mark task as done"
                              data-testid={`whos-turn-task-check-${t.id}`}>
                              {completing[t.id]
                                ? <Loader2 className="w-2.5 h-2.5 animate-spin text-muted-foreground" />
                                : <Check className="w-2.5 h-2.5" strokeWidth={3} />}
                            </button>
                            <span className="text-xs text-foreground/80">{t.title}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-foreground/80 mt-1">{deal.action}</p>
                    )}
                    {deal.detail && <p className="text-[10px] text-muted-foreground mt-0.5">{deal.detail}</p>}
                  </div>
                  <button onClick={() => navigate(`/projects/${deal.id}`)}
                    className="text-muted-foreground/30 hover:text-foreground transition-colors p-1 shrink-0">
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          );
        })
      )}
    </div>
  );
}
